import { Injectable } from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AbstractAppConfig } from '../../../app.config';
import { RequestOptionsBuilder, SearchView } from '../request';
import { SearchInput, SearchInputs } from '../../components/search-filters';
import { SearchResultView } from '../../domain/search';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpErrorService } from '../http';
import { SearchHttpParameterCodec } from './search-http-parameter-codec';

@Injectable()
export class SearchService {
  public static readonly V2_MEDIATYPE_SEARCH_INPUTS =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-search-input-details.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_SEARCH_INPUTS =
    'application/vnd.app.quickcase.store.data.api.ui-search-input-details.v2+json;charset=UTF-8';

  public static readonly VIEW_SEARCH = 'SEARCH';
  public static readonly VIEW_WORKBASKET = 'WORKBASKET';
  public static readonly FIELD_PREFIX = 'case.';
  private currentJurisdiction: string;
  private currentCaseType: string;

  constructor(private appConfig: AbstractAppConfig,
              private httpClient: HttpClient,
              private requestOptionsBuilder: RequestOptionsBuilder,
              private errorService: HttpErrorService) { }

  search(jurisdictionId: string, caseTypeId: string,
                metaCriteria: object, caseCriteria: object, view?: SearchView): Observable<SearchResultView> {
    const url = this.appConfig.getApiUrl() + `/caseworkers/:uid`
                                           + `/jurisdictions/${jurisdictionId}`
                                           + `/case-types/${caseTypeId}`
                                           + `/cases`;

    let options = this.requestOptionsBuilder.buildOptions(metaCriteria, caseCriteria, view);

    if (options && options.params) {
      options.params = new HttpParams({
        fromObject: options.params,
        encoder: new SearchHttpParameterCodec(),
      });
    }

    return this.httpClient
      .get<SearchResultView>(url, options)
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  getSearchInputs(jurisdictionId: string, caseTypeId: string): Observable<SearchInput[]> {
    let url = this.getSearchInputUrl(caseTypeId);
    const headers = new HttpHeaders({
      'Accept': [SearchService.V2_MEDIATYPE_SEARCH_INPUTS, SearchService.QC_V2_MEDIATYPE_SEARCH_INPUTS],
    });
    this.currentJurisdiction = jurisdictionId;
    this.currentCaseType = caseTypeId;
    return this.httpClient
      .get<SearchInputs>(url, { headers })
      .pipe(
        map(response => {
          let searchInputs = response.searchInputs;
          if (this.isDataValid(jurisdictionId, caseTypeId)) {
            searchInputs.forEach(item => {
              item.field.label = item.label;
            });
          } else {
            throw new Error('Response expired');
          }
          return searchInputs;
        }),
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  private getSearchInputUrl(caseTypeId: string): string {
    return `${this.appConfig.getCaseDataUrl()}/internal/case-types/${caseTypeId}/search-inputs`;
  }

  private isDataValid(jurisdictionId: string, caseTypeId: string): boolean {
    return this.currentJurisdiction === jurisdictionId && this.currentCaseType === caseTypeId
  }
}
